/**
 * Small pieces shared by the technology and university pages: the admin
 * state switch, breadcrumbs, the locked-section block (blurred placeholder
 * lines with no text — a locked section does not even receive its content),
 * the toast, clipboard, and a few icons.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Breadcrumb, PageState, SectionLock } from './api';

export const STATE_LABELS: Record<PageState, string> = {
  pre_login: 'Pre-Login',
  post_login: 'Post-Login',
  paid: 'Paid',
};

/** TRL scale wording, as the search filters word it (api.DIRECT_SEARCH_TRL_LABELS). */
export const TRL_LABELS: Record<number, string> = {
  1: 'Basic principles observed',
  2: 'Technology concept formulated',
  3: 'Experimental proof of concept',
  4: 'Technology validated in lab',
  5: 'Technology validated in relevant environment',
  6: 'Technology demonstrated in relevant environment',
  7: 'System prototype demonstration in operational environment',
  8: 'System complete and qualified',
  9: 'Actual system proven in operational environment',
};

export const UPGRADE_URL = '/?view=billing';

// ---------------------------------------------------------------------------
// Admin preview switch — rendered only when the response carries admin_preview
// ---------------------------------------------------------------------------

export const PreviewSwitch: React.FC<{
  states: PageState[];
  current: PageState;
  busy?: boolean;
  onPick: (s: PageState) => void;
}> = ({ states, current, busy, onPick }) => (
  <div className="tpg-preview" role="group" aria-label="Admin preview: page state">
    <span className="tpg-preview-label">Admin preview</span>
    <div className="tpg-seg">
      {(states.length ? states : (['pre_login', 'post_login', 'paid'] as PageState[])).map(s => (
        <button key={s} type="button" aria-pressed={s === current} disabled={busy} onClick={() => { if (s !== current) onPick(s); }}>
          {STATE_LABELS[s] || s}
        </button>
      ))}
    </div>
    {busy && <span className="tpg-spin" aria-label="Loading" />}
    <span className="tpg-muted" style={{ fontSize: 12 }}>Only admins see this switch.</span>
  </div>
);

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

export const Breadcrumbs: React.FC<{
  items: Breadcrumb[];
  onClick: (level: number, path: string, e: React.MouseEvent) => void;
}> = ({ items, onClick }) => {
  if (!items?.length) return null;
  return (
    <nav className="tpg-crumbs" aria-label="Breadcrumb">
      <ol>
        {items.map((b, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${i}:${b.path}`}>
              {last ? (
                <span aria-current="page" title={b.label}>{b.label}</span>
              ) : b.path ? (
                <a href={b.path} onClick={e => onClick(i, b.path, e)}>{b.label}</a>
              ) : (
                // No page to go to (e.g. university pages switched off).
                <span>{b.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Locked sections: blurred placeholder lines — no text at all
// ---------------------------------------------------------------------------
// A locked section never receives its real content, and its placeholder has
// no words either: text there (even blurred, even aria-hidden) would sit in
// the rendered DOM that search engines index, as keyword filler on every
// page. The lines are empty spans that CSS paints as blurred text.

const seedOf = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
};

/** Where a "word" starts in the line pattern (styles.tsx .tpg-filler-line,
 *  a 260px repeat), so every line begins on a word, not on a gap. */
const WORD_STARTS = [0, 70, 124, 222];

/** Deterministic line shapes for a seed (same every render): near-full
 *  lines and a shorter last one, each starting on a different word. */
const fillerLines = (seed: string, lines: number): Array<{ width: number; shift: number }> => {
  let x = seedOf(seed) || 1;
  const rnd = () => { x ^= x << 13; x ^= x >>> 17; x ^= x << 5; return ((x >>> 0) % 10000) / 10000; };
  const n = Math.max(1, Math.round(lines));
  return Array.from({ length: n }, (_, i) => ({
    width: n > 1 && i === n - 1 ? 30 + Math.round(rnd() * 35) : 90 + Math.round(rnd() * 10),
    shift: WORD_STARTS[Math.floor(rnd() * WORD_STARTS.length)],
  }));
};

export const LockedBlock: React.FC<{
  seed: string;
  lock?: SectionLock;
  fallbackMessage: string;
  lines?: number;
  onClick: () => void;
}> = ({ seed, lock, fallbackMessage, lines = 6, onClick }) => (
  <div className="tpg-locked">
    <div className="tpg-filler" aria-hidden="true">
      {fillerLines(seed, lines).map((l, i) => (
        <span key={i} className="tpg-filler-line" style={{ width: `${l.width}%`, backgroundPosition: `-${l.shift}px 0` }} />
      ))}
    </div>
    <div className="tpg-lock-veil">
      <button type="button" className="tpg-lock-btn" onClick={onClick}>
        <span aria-hidden="true">🔒</span>
        <span>{lock?.message || fallbackMessage}</span>
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Toast + clipboard
// ---------------------------------------------------------------------------

export function useToast(): [React.ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState('');
  const timer = useRef<number | null>(null);
  const show = useCallback((m: string) => {
    setMsg(m);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(''), 2600);
  }, []);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  const el = msg ? <div className="tpg-toast" role="status" aria-live="polite">{msg}</div> : null;
  return [el, show];
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall back below */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

export const hostOf = (url: string): string => {
  try { return new URL(url).host; } catch { return ''; }
};

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

export const ShareIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

export const BookmarkIcon: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4.5L5 21V4a1 1 0 0 1 1-1z" />
  </svg>
);

export const SearchIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
  </svg>
);
